/* eslint-disable @typescript-eslint/no-explicit-any */
import { getFromSessionStorage } from 'app/src/shared/helpers'
import libI18n from 'i18next'
import { useCallback, useMemo } from 'react'

type ExecuteErrorResponse = {
  fetchError?: any
}

type ExecuteResponse = {
  httpResponse?: Response
} & ExecuteErrorResponse

type DownloadFileErrorResponse = {
  blobError?: any
} & ExecuteErrorResponse

type DownloadFileResponse = {} & ExecuteResponse & DownloadFileErrorResponse

type ExecuteJsonErrorResponse = {
  jsonConversionError?: any
} & ExecuteErrorResponse

type ExecuteJsonResponse<T> = {
  jsonResponse: T | null
} & ExecuteResponse &
  ExecuteJsonErrorResponse

export type FetchParamsType = { input: string | URL | globalThis.Request; init?: RequestInit }

export type HttpClientType = {
  executeJson: <T>(fetchParams: FetchParamsType) => Promise<ExecuteJsonResponse<T>>
  execute: (fetchParams: FetchParamsType) => Promise<ExecuteResponse>
  fetch: typeof window.fetch
  downloadFile: (fetchParams: FetchParamsType, filename: string) => Promise<DownloadFileResponse>
  uploadFile: (fileUri: string, blob: Blob) => Promise<ExecuteResponse>
}

export const downloadBlob = (blob: Blob, filename: string, linkParent: HTMLElement = document.body): void => {
  const objectURL = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectURL
  link.download = filename
  linkParent?.appendChild(link)
  link.click()
  linkParent?.removeChild(link)
}

const handleSessionExpired = async (response: Response) => {
  if (response.status === 403) {
    let body
    try {
      body = await response.json()
    } catch {
      throw new Error('SESSION_EXPIRED')
    }
    if (!('type' in body)) {
      throw new Error('SESSION_EXPIRED')
    }
  }

  if (response.status === 401) {
    const body = await response.clone().json()
    if (body.type === 'urn:RegistrationRequired') {
      throw new Error('REGISTRATION_REQUIRED')
    }
  }

  return response
}

export const useHttpClient = (): HttpClientType => {
  const _fetch = async (input: string | URL | globalThis.Request, init?: RequestInit): Promise<Response> => {
    const campaignStorage = getFromSessionStorage('selectedCampaign')
    const headers = new Headers(init?.headers)
    headers.set('X-Welfare-Locale', libI18n?.language)
    if (campaignStorage) {
      headers.set('X-SelectedMembership', JSON.parse(campaignStorage).membershipId)
    }

    let atlasCall: boolean = false
    /*  if (typeof input === 'object' && (input as URL)?.origin?.startsWith('https://atlas.microsoft.com')) {
      atlasCall = true
    } else if (typeof input === 'object' && (input as globalThis.Request)?.url?.startsWith('https://atlas.microsoft.com')) {
      atlasCall = true
    } else */

    if (typeof input === 'string' && input?.startsWith('https://atlas.microsoft.com')) atlasCall = true
    const fetchResp: Response = await window.fetch(input, { ...init, headers })

    return atlasCall ? fetchResp : await handleSessionExpired(fetchResp)
  }

  if (!(window as any).customFetch) {
    ;(window as any).customFetch = _fetch
  }

  const uploadFile = (fileUri: string, blob: Blob) => {
    return execute({
      input: fileUri,
      init: { method: 'PUT', headers: { 'X-Ms-Blob-Type': 'blockBlob' }, body: blob },
    })
  }

  const downloadFile = async (fetchParams: FetchParamsType, filename: string): Promise<DownloadFileResponse> => {
    const executeResponse = await execute({ input: fetchParams.input, init: { ...fetchParams.init, redirect: 'follow' } })
    if (executeResponse.fetchError || !executeResponse.httpResponse || !executeResponse.httpResponse.ok) {
      return executeResponse
    }
    try {
      downloadBlob(await executeResponse?.httpResponse!.blob(), filename)
    } catch (blobError) {
      return { blobError, ...executeResponse }
    }

    return executeResponse
  }

  const execute = useCallback(async (fetchParams: FetchParamsType): Promise<ExecuteResponse> => {
    const { input, init } = fetchParams
    let httpResponse
    let fetchError
    try {
      httpResponse = await _fetch(input, init)
    } catch (err) {
      fetchError = err
    }

    return { httpResponse, fetchError }
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  const executeJson = async <T extends any>(fetchParams: FetchParamsType): Promise<ExecuteJsonResponse<T>> => {
    const { input, init } = fetchParams
    let jsonResponse: T | null = null
    let jsonConversionError
    const innerInit: RequestInit = init ? { ...init } : {}

    const headers = new Headers(innerInit.headers)

    headers.set('Accept', 'application/json')
    if (innerInit.body) headers.set('Content-Type', 'application/json')

    const resp = await execute({ input, init: { ...innerInit, headers } })

    if (!resp.fetchError && resp.httpResponse) {
      try {
        jsonResponse = await resp.httpResponse.json()
      } catch (err) {
        jsonConversionError = err
      }
    }

    return { ...resp, jsonResponse, jsonConversionError }
  }

  return {
    executeJson: useCallback(executeJson, [execute]),
    execute: useCallback(execute, [execute]),
    fetch: useMemo(() => _fetch, []),
    downloadFile: useCallback(downloadFile, [execute]),
    uploadFile: useCallback(uploadFile, [execute]),
  }
}
